import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { projectGalleryUrl } from "@/lib/api";

export const ProjectSlideshow = ({ project, interval = 4500 }) => {
  const ids = project.image_ids?.length ? project.image_ids : ["cover"];
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const many = ids.length > 1;

  useEffect(() => {
    if (!many || paused) return undefined;
    const t = setInterval(() => setIndex((i) => (i + 1) % ids.length), interval);
    return () => clearInterval(t);
  }, [many, paused, ids.length, interval]);

  const go = (dir) => setIndex((i) => (i + dir + ids.length) % ids.length);

  return (
    <div
      className="relative aspect-[16/10] overflow-hidden rounded-2xl border border-white/[0.08] bg-ink2"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      role="region"
      aria-roledescription="carousel"
      aria-label={`${project.title} screenshots`}
      data-testid={`portfolio-slideshow-${project.id}`}
    >
      <AnimatePresence initial={false} mode="popLayout">
        <motion.img
          key={ids[index]}
          src={projectGalleryUrl(project.id, ids[index])}
          alt={`${project.title}${project.category ? ` – ${project.category}` : ""} website screenshot ${index + 1} of ${ids.length}, designed and built by ForbesOS Websites`}
          className="absolute inset-0 h-full w-full object-cover object-top"
          initial={{ opacity: 0, scale: 1.02 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          loading="lazy"
          decoding="async"
          draggable={false}
        />
      </AnimatePresence>

      {many && (
        <>
          <button type="button" onClick={() => go(-1)} className="absolute left-3 top-1/2 -translate-y-1/2 grid h-10 w-10 place-items-center rounded-full bg-black/55 text-white backdrop-blur-sm opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity hover:bg-indigo-500" aria-label="Previous screenshot" data-testid={`slideshow-prev-${project.id}`}>
            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
          </button>
          <button type="button" onClick={() => go(1)} className="absolute right-3 top-1/2 -translate-y-1/2 grid h-10 w-10 place-items-center rounded-full bg-black/55 text-white backdrop-blur-sm opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity hover:bg-indigo-500" aria-label="Next screenshot" data-testid={`slideshow-next-${project.id}`}>
            <ChevronRight className="h-5 w-5" aria-hidden="true" />
          </button>
          <div className="absolute inset-x-0 bottom-3 flex items-center justify-center gap-1.5" role="tablist" aria-label="Choose screenshot">
            {ids.map((id, i) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={i === index}
                aria-label={`Screenshot ${i + 1}`}
                onClick={() => setIndex(i)}
                className={`h-1.5 rounded-full transition-[width,background-color] duration-300 ${i === index ? "w-6 bg-white" : "w-1.5 bg-white/40 hover:bg-white/70"}`}
                data-testid={`slideshow-dot-${project.id}-${i}`}
              />
            ))}
          </div>
          <span className="absolute right-3 top-3 rounded-full bg-black/55 px-2 py-0.5 font-mono text-[10px] text-white backdrop-blur-sm" data-testid={`slideshow-counter-${project.id}`}>{index + 1} / {ids.length}</span>
        </>
      )}
    </div>
  );
};
